import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { Chat, Message, Notification } from '../types';
import { useNavigate } from 'react-router-dom';

interface ChatContextType {
    chats: Chat[];
    activeChatId: string | null;
    messages: Message[];
    notifications: Notification[];
    unreadNotificationsCount: number;
    unreadMessagesCount: number;
    setActiveChatId: (id: string | null) => void;
    sendMessage: (chatId: string, content: string) => Promise<void>;
    createChat: (participantUserId: string) => Promise<string>;
    startChatWithContext: (participantUserId: string, initialMessage?: string) => Promise<string>;
    markChatAsRead: (chatId: string) => Promise<void>;
    markNotificationAsRead: (notificationId: string) => Promise<void>;
    loading: boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
    const [chats, setChats] = useState<Chat[]>([]);
    const [activeChatId, setActiveChatId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);
    const navigate = useNavigate();

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            if (!session?.user) setLoading(false);
        });

        const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user ?? null);
            if (!session?.user) {
                setChats([]);
                setMessages([]);
                setNotifications([]);
            }
        });

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!user) return;

        // Fetch initial data
        const fetchData = async () => {
            setLoading(true);

            // Fetch Notifications
            const { data: notifData } = await supabase
                .from('notifications')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(50);

            setNotifications(notifData || []);

            // Fetch Chats
            const { data: chatData, error: chatError } = await supabase
                .from('chats')
                .select(`
                    *,
                    participants:chat_participants(
                        user_id,
                        last_read_at,
                        profile:profiles(email)
                    )
                `)
                .order('updated_at', { ascending: false });

            if (chatError) {
                console.error('Error fetching chats:', chatError);
            }

            if (chatData) {
                setChats(chatData as any[]);
            }

            setLoading(false);
        };

        fetchData();

        // Subscribe to Realtime
        const channel = supabase.channel('user_updates');

        channel.on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
            },
            async (payload) => {
                const newMessage = payload.new as Message;

                setChats(prev => {
                    const chatExists = prev.some(c => c.id === newMessage.chat_id);
                    if (!chatExists) {
                        // Refetch needed
                        supabase
                            .from('chats')
                            .select(`
                *,
                participants:chat_participants(
                  user_id,
                  last_read_at,
                  profile:profiles(email)
                )
              `)
                            .eq('id', newMessage.chat_id)
                            .single()
                            .then(({ data }) => {
                                if (data) setChats(curr => [data as any, ...curr]);
                            });
                        return prev;
                    }
                    const updatedChats = prev.map(c =>
                        c.id === newMessage.chat_id ? { ...c, updated_at: newMessage.created_at } : c
                    ).sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
                    return updatedChats;
                });

                if (activeChatId === newMessage.chat_id) {
                    const { data: senderData } = await supabase
                        .from('profiles')
                        .select('email')
                        .eq('id', newMessage.sender_id)
                        .single();

                    const messageWithSender: Message = {
                        ...newMessage,
                        sender: senderData ? { email: senderData.email } : undefined
                    };
                    setMessages(prev => [...prev, messageWithSender]);
                }
            }
        )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user, activeChatId]);

    // Fetch messages when active chat changes
    useEffect(() => {
        if (!activeChatId) {
            setMessages([]);
            return;
        }

        const fetchMessages = async () => {
            const { data } = await supabase
                .from('messages')
                .select(`
           *,
           sender:profiles(email)
         `)
                .eq('chat_id', activeChatId)
                .order('created_at', { ascending: true });

            setMessages(data as any || []);
        };
        fetchMessages();
    }, [activeChatId]);

    const sendMessage = async (chatId: string, content: string) => {
        if (!user) return;

        await supabase.from('messages').insert({
            chat_id: chatId,
            sender_id: user.id,
            content
        });
    };

    const createChat = async (participantUserId: string) => {
        if (!user) throw new Error("Not logged in");

        const { data: chatId, error } = await supabase
            .rpc('create_chat', { participant_id: participantUserId });

        if (error) {
            console.error("Error creating chat:", error);
            throw error;
        }

        return chatId;
    };

    const startChatWithContext = async (participantUserId: string, initialMessage?: string) => {
        const chatId = await createChat(participantUserId);

        // If initial message provided, send it
        if (initialMessage) {
            // Check if chat is empty or just always send? 
            // Better to always send context if explicitly requested for a "About X" action.
            // But maybe debounce if one was sent recently? No, let's keep it simple.
            await sendMessage(chatId, initialMessage);
        }

        setActiveChatId(chatId);
        return chatId;
    };

    const markChatAsRead = async (chatId: string) => {
        if (!user) return;
        await supabase
            .from('chat_participants')
            .update({ last_read_at: new Date().toISOString() })
            .eq('chat_id', chatId)
            .eq('user_id', user.id);

        setChats(prev => prev.map(c => {
            if (c.id === chatId && c.participants) {
                return {
                    ...c,
                    participants: c.participants.map(p =>
                        p.user_id === user.id ? { ...p, last_read_at: new Date().toISOString() } : p
                    )
                };
            }
            return c;
        }));
    };

    const markNotificationAsRead = async (_notificationId: string) => {
        // Placeholder
    };

    const unreadMessagesCount = 0;
    const unreadNotificationsCount = 0;

    return (
        <ChatContext.Provider value={{
            chats,
            activeChatId,
            messages,
            notifications,
            unreadNotificationsCount,
            unreadMessagesCount,
            setActiveChatId,
            sendMessage,
            createChat,
            startChatWithContext,
            markChatAsRead,
            markNotificationAsRead,
            loading
        }}>
            {children}
        </ChatContext.Provider>
    );
}

export function useChat() {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}
